import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, addDays } from "date-fns";
import { it } from "date-fns/locale";
import { motion } from "framer-motion";
import {
  Search,
  ShieldCheck,
  ShieldX,
  AlertTriangle,
  FileWarning,
  Pencil,
  Stethoscope,
} from "lucide-react";
import { supabase } from "@/gestionale/integrations/supabase/client";
import type { Tables, TablesInsert, Database } from "@/gestionale/integrations/supabase/types";
import { Button } from "@/gestionale/components/ui/button";
import { Input } from "@/gestionale/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/gestionale/components/ui/select";
import { Badge } from "@/gestionale/components/ui/badge";
import { Card, CardContent } from "@/gestionale/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/gestionale/components/ui/table";
import { toast } from "sonner";
import PersonaDialog from "@/gestionale/components/persone/PersonaDialog";

type Persona = Tables<"persone">;
type TipoRuolo = Database["public"]["Enums"]["tipo_ruolo"];

type StatoCertificato = "valido" | "scadenza" | "scaduto" | "mancante";

const STATUS_META: Record<StatoCertificato, { label: string; className: string; icon: typeof ShieldCheck }> = {
  valido: {
    label: "Valido",
    className: "bg-green-500/15 text-green-700 border-green-500/30",
    icon: ShieldCheck,
  },
  scadenza: {
    label: "In scadenza",
    className: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    icon: AlertTriangle,
  },
  scaduto: {
    label: "Scaduto",
    className: "bg-red-500/15 text-red-700 border-red-500/30",
    icon: ShieldX,
  },
  mancante: {
    label: "Mancante",
    className: "bg-slate-500/15 text-slate-700 border-slate-500/30",
    icon: FileWarning,
  },
};

function getStatus(scadenza: string | null): StatoCertificato {
  if (!scadenza) return "mancante";
  const today = new Date();
  const warningLimit = addDays(today, 30);
  const date = new Date(scadenza);
  if (date < today) return "scaduto";
  if (date <= warningLimit) return "scadenza";
  return "valido";
}

export default function CertificatiMedici() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("tutti");
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);

  const { data: persone = [], isLoading } = useQuery({
    queryKey: ["certificati-medici-persone"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("persone")
        .select("*")
        .order("cognome", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: ruoliMap = {} } = useQuery({
    queryKey: ["certificati-medici-ruoli"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ruoli").select("persona_id, tipo_ruolo");
      if (error) throw error;
      const map: Record<string, TipoRuolo[]> = {};
      for (const ruolo of data) {
        if (!map[ruolo.persona_id]) map[ruolo.persona_id] = [];
        map[ruolo.persona_id].push(ruolo.tipo_ruolo);
      }
      return map;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ persona, ruoli }: { persona: TablesInsert<"persone"> & { id?: string }; ruoli: TipoRuolo[] }) => {
      if (!persona.id) throw new Error("Persona non valida");
      const { error } = await supabase.from("persone").update(persona).eq("id", persona.id);
      if (error) throw error;

      await supabase.from("ruoli").delete().eq("persona_id", persona.id);
      if (ruoli.length > 0) {
        const { error: ruoliError } = await supabase.from("ruoli").insert(
          ruoli.map((tipo_ruolo) => ({ persona_id: persona.id!, tipo_ruolo }))
        );
        if (ruoliError) throw ruoliError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificati-medici-persone"] });
      queryClient.invalidateQueries({ queryKey: ["certificati-medici-ruoli"] });
      queryClient.invalidateQueries({ queryKey: ["persone"] });
      queryClient.invalidateQueries({ queryKey: ["ruoli"] });
      queryClient.invalidateQueries({ queryKey: ["persone-count"] });
      toast.success("Certificato medico aggiornato");
      setEditingPersona(null);
    },
    onError: (error) => toast.error("Errore: " + error.message),
  });

  const personeSportive = useMemo(
    () =>
      persone.filter((persona) => {
        const ruoli = ruoliMap[persona.id] || [];
        return ruoli.includes("Atleta") || ruoli.includes("Giocatore");
      }),
    [persone, ruoliMap]
  );

  const counts = useMemo(() => {
    const result: Record<StatoCertificato, number> = {
      valido: 0,
      scadenza: 0,
      scaduto: 0,
      mancante: 0,
    };
    personeSportive.forEach((persona) => {
      result[getStatus(persona.certificato_medico_scadenza)]++;
    });
    return result;
  }, [personeSportive]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return personeSportive.filter((persona) => {
      const ruoli = ruoliMap[persona.id] || [];
      const stato = getStatus(persona.certificato_medico_scadenza);

      if (filtroTipo !== "tutti" && !ruoli.includes(filtroTipo as TipoRuolo)) return false;
      if (filtroStato !== "tutti" && stato !== filtroStato) return false;

      if (!q) return true;
      return (
        persona.nome.toLowerCase().includes(q) ||
        persona.cognome.toLowerCase().includes(q)
      );
    });
  }, [personeSportive, ruoliMap, search, filtroTipo, filtroStato]);

  const statCards = [
    { key: "valido" as const, title: "Validi", value: counts.valido, icon: ShieldCheck, className: "text-green-600", bg: "bg-green-500/10" },
    { key: "scadenza" as const, title: "In scadenza", value: counts.scadenza, icon: AlertTriangle, className: "text-amber-600", bg: "bg-amber-500/10" },
    { key: "scaduto" as const, title: "Scaduti", value: counts.scaduto, icon: ShieldX, className: "text-red-600", bg: "bg-red-500/10" },
    { key: "mancante" as const, title: "Mancanti", value: counts.mancante, icon: FileWarning, className: "text-slate-600", bg: "bg-slate-500/10" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary/10">
            <Stethoscope className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Certificati Medici</h1>
            <p className="text-muted-foreground mt-1">
              Controllo delle scadenze di giocatori e atleti
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card
            key={stat.key}
            className={`glass-card cursor-pointer transition-shadow hover:shadow-md ${filtroStato === stat.key ? "ring-2 ring-primary/30" : ""}`}
            onClick={() => setFiltroStato(filtroStato === stat.key ? "tutti" : stat.key)}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <stat.icon className={`h-5 w-5 ${stat.className}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome o cognome..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtra per tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti</SelectItem>
            <SelectItem value="Giocatore">Giocatori</SelectItem>
            <SelectItem value="Atleta">Atleti</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli stati</SelectItem>
            <SelectItem value="valido">Validi</SelectItem>
            <SelectItem value="scadenza">In scadenza</SelectItem>
            <SelectItem value="scaduto">Scaduti</SelectItem>
            <SelectItem value="mancante">Mancanti</SelectItem>
          </SelectContent>
        </Select>

        {(search || filtroTipo !== "tutti" || filtroStato !== "tutti") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setFiltroTipo("tutti");
              setFiltroStato("tutti");
            }}
          >
            Azzera filtri
          </Button>
        )}
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            Caricamento certificati...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="p-4 rounded-2xl bg-primary/10 mb-4">
              <Stethoscope className="h-10 w-10 text-primary" />
            </div>
            <p className="text-lg font-semibold">Nessun certificato trovato</p>
            <p className="text-sm text-muted-foreground mt-1">
              Prova a modificare i filtri o verifica i ruoli delle persone.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Persona</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((persona) => {
                const stato = getStatus(persona.certificato_medico_scadenza);
                const meta = STATUS_META[stato];
                const StatusIcon = meta.icon;
                const ruoli = ruoliMap[persona.id] || [];

                return (
                  <TableRow key={persona.id}>
                    <TableCell className="font-medium">
                      {persona.cognome} {persona.nome}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {ruoli.filter((r) => r === "Atleta" || r === "Giocatore").map((ruolo) => (
                          <Badge key={ruolo} variant="outline">
                            {ruolo}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {persona.certificato_medico_scadenza
                        ? format(new Date(persona.certificato_medico_scadenza), "dd/MM/yyyy", { locale: it })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={meta.className}>
                        <StatusIcon className="h-3.5 w-3.5 mr-1.5" />
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Modifica certificato"
                        onClick={() => setEditingPersona(persona)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <PersonaDialog
        open={!!editingPersona}
        onOpenChange={(open) => {
          if (!open) setEditingPersona(null);
        }}
        persona={editingPersona}
        personaRuoli={editingPersona ? (ruoliMap[editingPersona.id] || []) : []}
        onSave={(persona, ruoli) => updateMutation.mutate({ persona, ruoli })}
        isSaving={updateMutation.isPending}
      />
    </motion.div>
  );
}
